import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce, useDebouncedValue, useLatestRef, usePersistentCallback } from './hooks';
/**
 * Hook to manage auto-scroll behavior in a scrollable container
 *
 * Features:
 * - Tracks user scroll position to determine if at bottom
 * - Uses ResizeObserver to detect when content size changes
 * - Auto-scrolls to bottom when enabled
 * - Respects user's manual scroll (won't auto-scroll if user has scrolled up)
 * - If userScrollThreshold is 0, always auto-scrolls regardless of user position
 *
 * @example
 * ```tsx
 * const [scroller, setScroller] = useState<HTMLElement | null>(null);
 * const [observed, setObserved] = useState<HTMLElement | null>(null);
 *
 * const { atBottom, scrollToBottom } = useAutoScroll({
 *   scrollerElement: scroller,
 *   observedElement: observed,
 *   enabled: isAgentWorking,
 *   scrollBehavior: 'smooth',
 *   userScrollThreshold: 50 // Set to 0 to always auto-scroll
 * });
 *
 * // In component:
 * <div ref={setScroller}>
 *   <div ref={setObserved}>
 *     // content
 *   </div>
 * </div>
 * ```
 */
export function useAutoScroll({ scrollerElement, observedElement, enabled, scrollBehavior = 'smooth', userScrollThreshold = 0, scrollDebounceTime = 300 }) {
    const [atBottom, setAtBottom] = useState(true);
    const [canAutoScroll, setCanAutoScroll] = useState(true);
    const [debouncedAtBottom] = useDebouncedValue(atBottom, scrollDebounceTime);
    const lastScrollTop = useRef(0);
    const canAutoScrollRef = useLatestRef(canAutoScroll);
    const enabledRef = useLatestRef(enabled);
    // Track if we're actively scrolling to bottom and what behavior to use
    const scrollingToBottomBehaviorRef = useRef(null);
    const scrollToBottom = usePersistentCallback((behavior) => {
        if (!scrollerElement)
            return;
        const scrollBehaviorToUse = behavior !== null && behavior !== void 0 ? behavior : scrollBehavior;
        scrollingToBottomBehaviorRef.current = scrollBehaviorToUse;
        // Scroll scroller to its maximum scroll height
        scrollerElement.scrollTo({ top: scrollerElement.scrollHeight, behavior: scrollBehaviorToUse });
    });
    /**
     * Follows the content down, unless the reader has scrolled away in the meantime.
     * Re-checks the guard right before the scroll runs, since the resize that triggers
     * this can land before the reader's own scroll event does.
     */
    const followContentDown = usePersistentCallback((behavior) => {
        if (!canAutoScrollRef.current || !enabledRef.current)
            return;
        scrollToBottom(behavior);
    });
    const debouncedScrollToBottom = useDebounce(followContentDown, scrollDebounceTime);
    const isAtBottom = usePersistentCallback(() => {
        if (!scrollerElement)
            return true;
        const { scrollTop, scrollHeight, clientHeight } = scrollerElement;
        const scrollBottom = scrollHeight - scrollTop - clientHeight;
        return scrollBottom <= userScrollThreshold || scrollHeight <= clientHeight;
    });
    const handleScroll = usePersistentCallback(() => {
        // If threshold is 0, always consider at bottom and can autoscroll
        if (userScrollThreshold === 0 || !scrollerElement)
            return;
        const { scrollTop } = scrollerElement;
        const newIsAtBottom = isAtBottom();
        // If we reached bottom, we're no longer actively scrolling
        if (newIsAtBottom) {
            scrollingToBottomBehaviorRef.current = null;
        }
        // During programmatic scrolls, check if user scrolled up to interrupt
        if (scrollingToBottomBehaviorRef.current !== null && scrollTop < lastScrollTop.current) {
            // User scrolled up during programmatic scroll - cancel auto-scroll immediately
            scrollingToBottomBehaviorRef.current = null;
            setCanAutoScroll(false);
            setAtBottom(false);
        }
        else if (scrollingToBottomBehaviorRef.current !== null && scrollTop > lastScrollTop.current && !newIsAtBottom) {
            // Still scrolling down programmatically but not at bottom yet - don't update canAutoScroll
            // This prevents premature cancellation during smooth scroll
        }
        else if (scrollingToBottomBehaviorRef.current === null) {
            // Normal user scroll or programmatic scroll complete - update based on position
            setCanAutoScroll(newIsAtBottom);
            setAtBottom(newIsAtBottom);
        }
        lastScrollTop.current = scrollTop;
    });
    // Set up scroll event listener when scroller is available (skip if threshold is 0)
    useEffect(() => {
        // If threshold is 0, we don't need to track scroll events
        if (userScrollThreshold === 0)
            return;
        const scrollAbortCtrl = new AbortController();
        if (scrollerElement && scrollerElement instanceof HTMLElement) {
            scrollerElement.addEventListener('scroll', handleScroll, { signal: scrollAbortCtrl.signal });
        }
        return () => {
            scrollAbortCtrl.abort();
        };
    }, [scrollerElement, handleScroll, userScrollThreshold]);
    // Set up ResizeObserver to auto-scroll when content changes
    useEffect(() => {
        if (!observedElement)
            return;
        if (scrollingToBottomBehaviorRef.current === null) {
            setAtBottom(isAtBottom());
        }
        const handleContentChange = () => {
            if (!canAutoScrollRef.current || !enabledRef.current)
                return;
            debouncedScrollToBottom(scrollBehavior);
        };
        // ResizeObserver: Detects when content size changes (expand/collapse, images loading, etc.)
        const resizeObserver = new ResizeObserver(handleContentChange);
        resizeObserver.observe(observedElement);
        return () => {
            resizeObserver.disconnect();
        };
    }, [observedElement, debouncedScrollToBottom, scrollBehavior, canAutoScrollRef, enabledRef, isAtBottom]);
    return useMemo(() => ({
        atBottom: debouncedAtBottom,
        scrollToBottom
    }), [debouncedAtBottom, scrollToBottom]);
}
